import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { DataVisibleTableMode } from '../models/dataVisible.types'
import type {
  ReportDataLoader,
  ReportDataState,
  ReportResponse,
  ReportRow,
  ReportSpec,
} from '../models/report.types'
import { requestSignature } from '../service/reportRequestSignature'

import type { RenderSnapshotSpec } from './useSettledReportRenderSnapshot'

import type { UseDataVisibleChartState } from './useDataVisibleChartState'
import {
  useDataVisibleReportRequest,
  type UseDataVisibleReportRequestOptions,
} from './useDataVisibleReportRequest'
import { useReportData, type UseReportData } from './useReportData'

/** The half of a request both renderers of one surface state identically. */
export type SharedReportRequestOptions = Pick<
  UseDataVisibleReportRequestOptions,
  | 'reportId'
  | 'partitionName'
  | 'defaultPostData'
  | 'chartCatalogLoading'
  | 'chartUnavailable'
  | 'chartFieldsValid'
  | 'filters'
  | 'cardStatus'
  | 'sort'
  | 'columns'
  | 'partitionOnly'
  | 'countUniqueDimensions'
  | 'edges'
  | 'targetCurrency'
  | 'addCompare'
>

export interface UseDataVisibleChartRequestOptions {
  /** Only a `chart-table` surface draws a chart beside a table of its own. */
  enabled: MaybeRefOrGetter<boolean>
  chart: UseDataVisibleChartState
  shared: SharedReportRequestOptions
  /** The table's request and the reader that answers it — the chart's fallback source. */
  tableSpec: MaybeRefOrGetter<ReportSpec | null>
  tableReport: UseReportData
  /** Rendered table rows, which the chart reads instead whenever it shares the table's request. */
  tableRows: MaybeRefOrGetter<readonly ReportRow[]>
  tableBreakdownField: MaybeRefOrGetter<string | null>
  workspaceIds: MaybeRefOrGetter<readonly (number | string)[]>
  dataLoader?: ReportDataLoader
}

export interface UseDataVisibleChartRequest {
  /** The chart's own request, or null while the table's response already answers it. */
  spec: ComputedRef<ReportSpec | null>
  hasOwnRequest: ComputedRef<boolean>
  /** What the chart draws: its own rows, or the table's — flattened past a shared breakdown. */
  rows: ComputedRef<readonly ReportRow[]>
  response: ComputedRef<ReportResponse | null>
  state: ComputedRef<ReportDataState>
  settledSpec: ComputedRef<ReportSpec | null>
  /**
   * The surface as a whole, for the render snapshot: it may only replace what is on screen once
   * BOTH reads that feed it have answered their current specs, or the table would repaint against
   * a chart still drawing the previous question's data.
   */
  surfaceState: ComputedRef<ReportDataState>
  surfaceCurrentSpec: ComputedRef<RenderSnapshotSpec>
  surfaceSettledSpec: ComputedRef<RenderSnapshotSpec>
}

/**
 * The chart half of a `chart-table` surface: one question, or two.
 *
 * The chart rides along on the table's response whenever both ask the controller the same thing —
 * differing row windows and metric lists don't count, since neither changes the grouping (and
 * `metrics` no longer reaches the wire at all). A chart field the table doesn't show, or a table
 * breakdown the chart doesn't share, does: those are two different reads and go out as two.
 */
export function useDataVisibleChartRequest(
  options: UseDataVisibleChartRequestOptions,
): UseDataVisibleChartRequest {
  const { chart } = options
  // Built unconditionally so it can be compared with the table's; `spec` below is what actually
  // reaches the controller.
  const request = useDataVisibleReportRequest({
    ...options.shared,
    view: () => 'chart',
    viewUnavailable: computed(
      () =>
        !toValue(options.enabled) || chart.viewUnavailable.value || chart.activeMeta.value === null,
    ),
    tableUnavailable: () => false,
    tableDimensions: () => [],
    tableMetrics: () => [],
    chartDimensions: chart.requestDimensions,
    chartTimeDimensions: chart.validationTimeDimensions,
    chartMetrics: chart.requestMetrics,
    chartActive: () => true,
    chartLimit: computed(() => chart.activeMeta.value?.defaultLimit),
    // A chart never presents through a table presenter, so it is always the standard mode.
    tableMode: (): DataVisibleTableMode => 'standard',
    trend: () => undefined,
    breakdownField: () => null,
    tablePresenterAvailable: () => false,
    // A chart reads its whole (limited) result set at once; paging belongs to the table.
    pagination: () => false,
  })
  const spec = computed<ReportSpec | null>(() =>
    toValue(options.enabled) &&
    requestSignature(request.spec.value) !== requestSignature(toValue(options.tableSpec))
      ? request.spec.value
      : null,
  )
  const hasOwnRequest = computed(() => spec.value !== null)
  const report = useReportData(spec, {
    workspaceIds: options.workspaceIds,
    ...(options.dataLoader ? { dataLoader: options.dataLoader } : {}),
  })
  const source = computed(() => (hasOwnRequest.value ? report : options.tableReport))
  const tableSettledSpec = computed(
    () => options.tableReport.settledSpec?.value ?? toValue(options.tableSpec),
  )
  const settledSpec = computed(() =>
    hasOwnRequest.value ? (report.settledSpec?.value ?? spec.value) : tableSettledSpec.value,
  )

  return {
    spec,
    hasOwnRequest,
    rows: computed(() => {
      if (hasOwnRequest.value) return report.rows.value
      const tableRows = toValue(options.tableRows)
      // A shared request that carries the table's breakdown answers with one parent row per
      // category; the chart draws the edge rows underneath them.
      if (!toValue(options.enabled) || !toValue(options.tableBreakdownField)) return tableRows
      return tableRows.flatMap((row) =>
        Array.isArray(row.edges) && row.edges.length > 0 ? row.edges : [row],
      )
    }),
    response: computed(() => source.value.response.value),
    state: computed(() => source.value.state.value),
    settledSpec,
    surfaceCurrentSpec: computed(() =>
      hasOwnRequest.value ? [toValue(options.tableSpec), spec.value] : toValue(options.tableSpec),
    ),
    surfaceSettledSpec: computed(() =>
      hasOwnRequest.value ? [tableSettledSpec.value, settledSpec.value] : tableSettledSpec.value,
    ),
    surfaceState: computed<ReportDataState>(() => {
      const tableState = options.tableReport.state.value
      if (!hasOwnRequest.value) return tableState
      if (tableState === 'loading' || report.state.value === 'loading') return 'loading'
      if (report.state.value === 'idle') return 'loading'
      // The table owns the surface's empty/ready reading; a chart with nothing to draw renders its
      // own empty state through a null chart spec.
      return tableState
    }),
  }
}
